import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SlickCarouselComponent, SlickCarouselModule } from 'ngx-slick-carousel';
import { PipesModule } from '@core/pipes/pipes.module';
import { CarouselNavigationButtonComponent } from '@shared/buttons/carousel-navigation-button/carousel-navigation-button.component';
import { TranslateModule } from '@ngx-translate/core';

export interface DynamicCarousel {
	title?: string;
	description?: string;
	slides: string[];
}

@Component({
	selector: 'app-dynamic-slider-modal',
	standalone: true,
	imports: [SlickCarouselModule, PipesModule, CarouselNavigationButtonComponent, TranslateModule],
	templateUrl: './dynamic-carousel-modal.component.html',
	styleUrls: ['./dynamic-carousel-modal.component.scss', 'dynamic-carousel-modal.responsive.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicCarouselModalComponent {
	static readonly ID = crypto.randomUUID();

	@ViewChild('slickCarousel') slickCarousel: SlickCarouselComponent;

	readonly data = inject<DynamicCarousel>(MAT_DIALOG_DATA);

	private readonly dialogRef = inject(MatDialogRef<DynamicCarouselModalComponent>);

	private totalSlides = signal<number | undefined>(undefined);
	protected currentSlideIndex = signal<number>(0);
	protected isLastSlide = computed(() => this.currentSlideIndex() === this.totalSlides() - 1);

	protected readonly sliderConfig = {
		slidesToShow: 1,
		slidesToScroll: 1,
		dots: true,
		infinite: false,
		swipe: false,
		arrows: false,
		autoplay: false,
		adaptiveHeight: true,
		customPaging: () => {
			return '<div class="slick-dot-custom"></div>';
		},
	};

	protected close(): void {
		this.dialogRef.close();
	}

	protected slickInit(): void {
		this.totalSlides.set(this.data.slides.length);
		this.updateSliderHeightOnSliderInit();
	}

	private updateSliderHeightOnSliderInit(): void {
		const firstSlideImg = this.slickCarousel.slides[0]?.el?.nativeElement.querySelector('img');

		if (!firstSlideImg) {
			return;
		}

		firstSlideImg.addEventListener(
			'load',
			() => {
				this.slickCarousel.slickGoTo(this.currentSlideIndex());
			},
			{ once: true },
		);
	}

	protected afterChange<T extends { currentSlide: number }>({ currentSlide }: T): void {
		this.currentSlideIndex.set(currentSlide);
	}
}
